/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { ArrowRight, CreditCard, Lock, Scale, Wallet } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { api } from '../../api';
import type { BalanceHold, HoldStatus, Payment, StatusTotal } from '../../api/areas/commerce';
import { Callout, DataTable, FilterBar, Section, StatCard, StatGrid, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { DomainLink } from '../../components/Ids';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { CopyText, EmptyState, Loading } from '../../components/States';
import { formatCount, formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import type { ToneMap } from '../accounts/shared';
import {
  AccountOwner,
  AccountRef,
  Amount,
  countFor,
  CustomerRef,
  DOC_TYPE,
  FilterSelect,
  HOLD_STATUS,
  HoldRef,
  JournalRef,
  JournalTable,
  Label,
  ORDER_ITEM_STATUS,
  OrderRef,
  Pager,
  PAYMENT_STATUS,
  PaymentRef,
  quotePath,
  RefreshButton,
  REFUND_STATUS,
  RichText,
  setParam,
  Status,
  useEnumLabel,
  useKeyset,
  When,
} from './shared';

function SectionLink({ to, children }: { to: string; children: string }) {
  return (
    <Link to={to} className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
      {children} <ArrowRight className="size-3.5" aria-hidden="true" />
    </Link>
  );
}

function StatusTotals<S extends string>({ title, rows, group, tones, to }: { title: string; rows: StatusTotal<S>[]; group: string; tones: ToneMap; to?: string }) {
  const { t } = useTranslation('commerce');
  const columns: Column<StatusTotal<S>>[] = [
    { key: 'status', header: t('billing.col.status'), cell: (r) => <Status group={group} tones={tones} value={r.status} /> },
    { key: 'count', header: t('billing.col.count'), numeric: true, cell: (r) => formatCount(r.count) },
    { key: 'amount', header: t('billing.col.amount'), numeric: true, cell: (r) => <Amount amount={r.amount} currency={r.currency} /> },
  ];
  return (
    <Section title={title} flush actions={to ? <SectionLink to={to}>{t('billing.viewAll')}</SectionLink> : null}>
      <DataTable columns={columns} rows={rows} rowKey={(r) => `${r.status}/${r.currency}`} empty={<p className="text-sm text-muted-foreground">{t('billing.noneRecorded')}</p>} />
    </Section>
  );
}

function HoldsList() {
  const { t } = useTranslation('commerce');
  const [params, setParams] = useSearchParams();
  const raw = params.get('hold_status');
  const status = (raw === null ? 'ACTIVE' : raw === 'ANY' ? '' : raw) as HoldStatus | '';
  const pager = useKeyset(status);
  const result = useApi((signal) => api.listHolds({ status: status || undefined, limit: 50, before: pager.before }, { signal }), [status, pager.before]);
  const columns: Column<BalanceHold>[] = [
    { key: 'hold', header: t('billing.holdCol.hold'), cell: (h) => <HoldRef hold={h} /> },
    { key: 'account', header: t('billing.holdCol.account'), cell: (h) => <AccountRef id={h.account_id} code={h.account_code} ownerName={h.owner_name} currency={h.currency} /> },
    { key: 'status', header: t('billing.holdCol.status'), cell: (h) => <Status group="holdStatus" tones={HOLD_STATUS} value={h.status} /> },
    { key: 'amount', header: t('billing.holdCol.amount'), numeric: true, cell: (h) => <Amount amount={h.amount} currency={h.currency} /> },
    { key: 'created', header: t('billing.holdCol.created'), cell: (h) => <When at={h.created_at} /> },
    {
      key: 'closes',
      header: t('billing.holdCol.closes'),
      cell: (h) =>
        h.closed_at ? (
          <When at={h.closed_at} />
        ) : h.expires_at ? (
          <span title={formatDateTime(h.expires_at)} className="whitespace-nowrap">
            {t('common.expires')} <When at={h.expires_at} />
          </span>
        ) : (
          '—'
        ),
    },
  ];
  return (
    <Section title={t('billing.holdsList')} flush>
      <div className="px-5">
        <FilterBar>
          <FilterSelect label={t('common.status')} value={status} options={Object.keys(HOLD_STATUS)} group="holdStatus" onChange={(v) => setParams(setParam(params, 'hold_status', v || 'ANY'))} />
        </FilterBar>
      </div>
      {result.status === 'loading' ? (
        <div className="px-5">
          <Loading rows={3} />
        </div>
      ) : null}
      {result.status === 'error' ? (
        <div className="px-5 pb-5">
          <DataError error={result.error} onRetry={result.reload} title={t('billing.holdsLoadError')} />
        </div>
      ) : null}
      {result.status === 'success' ? (
        <>
          <DataTable columns={columns} rows={result.data.items} rowKey={(h) => h.id} empty={<EmptyState title={t('billing.holdsEmpty')} />} />
          <Pager cursors={pager.cursors} next={result.data.next_before} onNewer={pager.newer} onOlder={pager.older} />
        </>
      ) : null}
    </Section>
  );
}

/** `/billing`: wallet balances, reserved funds, payment/refund/invoice totals and the latest ledger activity. */
export function BillingPage() {
  const { t } = useTranslation('commerce');
  useDocumentTitle(t('billing.title'));
  const result = useApi((signal) => api.getBillingOverview({ signal }), []);

  const paymentColumns: Column<Payment>[] = [
    { key: 'payment', header: t('payments.col.payment'), cell: (p) => <PaymentRef payment={p} /> },
    { key: 'customer', header: t('payments.col.customer'), cell: (p) => <CustomerRef id={p.customer_id} name={p.customer_name} /> },
    { key: 'purpose', header: t('payments.col.purpose'), cell: (p) => <Label group="paymentPurpose" value={p.purpose} /> },
    { key: 'status', header: t('payments.col.status'), cell: (p) => <Status group="paymentStatus" tones={PAYMENT_STATUS} value={p.status} /> },
    { key: 'amount', header: t('payments.col.amount'), numeric: true, cell: (p) => <Amount amount={p.amount} currency={p.currency} /> },
    { key: 'created', header: t('payments.col.created'), cell: (p) => <When at={p.created_at} /> },
  ];
  const invoiceColumns: Column<{ document_type: string; currency: string; count: number; total: string }>[] = [
    { key: 'doc', header: t('billing.col.document'), cell: (i) => <Status group="docType" tones={DOC_TYPE} value={i.document_type} /> },
    { key: 'count', header: t('billing.col.count'), numeric: true, cell: (i) => formatCount(i.count) },
    { key: 'amount', header: t('billing.col.amount'), numeric: true, cell: (i) => <Amount amount={i.total} currency={i.currency} /> },
  ];

  let body = null;
  if (result.status === 'loading') body = <Loading rows={4} />;
  else if (result.status === 'error') body = <DataError error={result.error} onRetry={result.reload} title={t('billing.loadError')} />;
  else {
    const d = result.data;
    const activeHolds = d.holds.filter((h) => h.status === 'ACTIVE');
    const openHolds = countFor(d.holds, ['ACTIVE']);
    const failed = countFor(d.payments, ['FAILED']);
    const unknown = countFor(d.payments, ['UNKNOWN']);
    const balanced = d.ledger_imbalanced_entries === 0;
    body = (
      <div className="flex flex-col gap-6">
        <StatGrid>
          {d.wallets.map((w) => (
            <StatCard
              key={w.currency}
              icon={Wallet}
              label={t('billing.wallets', { currency: w.currency })}
              value={<Amount amount={w.balance_total} currency={w.currency} />}
              hint={
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                  <dt>{t('billing.availableLabel')}</dt>
                  <dd className="text-right">
                    <Amount amount={w.available_total} currency={w.currency} className="font-medium text-foreground" />
                  </dd>
                  <dt>{t('billing.heldLabel')}</dt>
                  <dd className="text-right">
                    <Amount amount={w.held_total} currency={w.currency} />
                  </dd>
                  <dt className="col-span-2">{t('billing.walletCount', { count: w.account_count })}</dt>
                </dl>
              }
              to="/ledger?owner_type=CUSTOMER"
              linkLabel={t('billing.walletLink')}
            />
          ))}
          <StatCard
            icon={Lock}
            label={t('billing.openHolds')}
            value={formatCount(openHolds)}
            tone={openHolds > 0 ? 'pending' : 'neutral'}
            hint={
              openHolds > 0 ? (
                <div className="space-y-0.5">
                  <div>{t('billing.openHoldsHint')}</div>
                  {activeHolds.map((h) => (
                    <div key={h.currency}>
                      <Amount amount={h.amount} currency={h.currency} className="font-medium text-foreground" />
                    </div>
                  ))}
                </div>
              ) : (
                t('billing.noOpenHolds')
              )
            }
          />
          <StatCard
            icon={CreditCard}
            label={t('billing.failedPayments')}
            value={formatCount(failed)}
            tone={failed > 0 ? 'crit' : unknown > 0 ? 'serious' : 'ok'}
            hint={unknown > 0 ? t('billing.unknownPayments', { count: unknown }) : failed === 0 ? t('billing.noFailedPayments') : undefined}
            to={failed > 0 ? '/payments?status=FAILED' : unknown > 0 ? '/payments?status=UNKNOWN' : undefined}
            linkLabel={t('billing.viewAll')}
          />
          <StatCard
            icon={Scale}
            label={t('billing.imbalance')}
            value={formatCount(d.ledger_imbalanced_entries)}
            tone={balanced ? 'ok' : 'crit'}
            status={balanced ? t('billing.balanced') : t('billing.imbalanced')}
            hint={t('billing.imbalanceHint')}
            to="/ledger"
            linkLabel={t('billing.ledgerLink')}
          />
        </StatGrid>
        {d.wallets.length === 0 ? <p className="text-sm text-muted-foreground">{t('billing.noWallets')}</p> : null}

        <div className="grid gap-6 lg:grid-cols-2 [&>*]:min-w-0">
          <StatusTotals title={t('billing.payments')} rows={d.payments} group="paymentStatus" tones={PAYMENT_STATUS} to="/payments" />
          <StatusTotals title={t('billing.holds')} rows={d.holds} group="holdStatus" tones={HOLD_STATUS} />
          <StatusTotals title={t('billing.refunds')} rows={d.refunds} group="refundStatus" tones={REFUND_STATUS} to="/payments#refunds" />
          <Section title={t('billing.invoices')} flush actions={<SectionLink to="/invoices">{t('billing.viewAll')}</SectionLink>}>
            <DataTable columns={invoiceColumns} rows={d.invoices} rowKey={(i) => `${i.document_type}/${i.currency}`} empty={<p className="text-sm text-muted-foreground">{t('billing.noneIssued')}</p>} />
          </Section>
        </div>

        <HoldsList />

        <Section title={t('billing.latestEntries')} flush actions={<SectionLink to="/ledger?tab=journal">{t('billing.viewAll')}</SectionLink>}>
          <JournalTable entries={d.recent_entries} />
        </Section>

        <Section title={t('billing.latestPayments')} flush actions={<SectionLink to="/payments">{t('billing.viewAll')}</SectionLink>}>
          <DataTable columns={paymentColumns} rows={d.recent_payments} rowKey={(p) => p.id} empty={<p className="text-sm text-muted-foreground">{t('billing.noPayments')}</p>} />
        </Section>
      </div>
    );
  }

  return (
    <>
      <PageHeader title={t('billing.title')} crumbs={[{ label: t('crumb') }]} subtitle={<RichText k="billing.subtitle" />} actions={<RefreshButton onClick={result.reload} />} />
      <Callout tone="warn" title={t('billing.limitTitle')} className="mb-6">
        {t('billing.limit')}
      </Callout>
      {body}
    </>
  );
}

/** `/holds/:holdId`: funds reserved on a wallet — what for, on which account, and what was posted. */
export function HoldDetailPage() {
  const { t } = useTranslation('commerce');
  const label = useEnumLabel('commerce');
  const { holdId = '' } = useParams();
  useDocumentTitle(t('hold.title'));
  const result = useApi((signal) => api.getHold(holdId, { signal }), [holdId]);
  const crumbs = [{ label: t('crumb') }, { label: t('billing.title'), to: '/billing' }];
  if (result.status === 'loading') return <Loading rows={4} />;
  if (result.status === 'error') {
    return (
      <>
        <PageHeader title={t('hold.title')} crumbs={crumbs} />
        <DataError error={result.error} onRetry={result.reload} title={t('hold.loadError')} />
      </>
    );
  }
  const { hold: h, account, order_item: item, journal_entries } = result.data;
  const capture = journal_entries.find((e) => e.id === h.capture_entry_id);
  return (
    <>
      <PageHeader
        title={h.domain_name ? t('hold.titleFor', { domain: h.domain_name }) : t('hold.title')}
        crumbs={[...crumbs, { label: h.domain_name ?? t('hold.title') }]}
        subtitle={label('holdPurpose', h.purpose)}
        actions={<RefreshButton onClick={result.reload} />}
      />
      <div className="flex flex-col gap-6">
        <StatGrid className="xl:grid-cols-3">
          <StatCard label={t('hold.stat.status')} value={label('holdStatus', h.status)} tone={HOLD_STATUS[h.status] ?? 'neutral'} />
          <StatCard label={t('hold.stat.amount')} value={<Amount amount={h.amount} currency={h.currency} />} />
          <StatCard label={t('hold.stat.available')} value={account ? <Amount amount={account.available} currency={account.currency} /> : '—'} tone="ok" />
        </StatGrid>
        {h.status === 'ACTIVE' ? <Callout tone="info">{t('hold.explain')}</Callout> : null}
        <Section>
          <KeyValue
            columns={3}
            rows={[
              [t('common.status'), <Status key="s" group="holdStatus" tones={HOLD_STATUS} value={h.status} />],
              [t('common.amount'), <Amount key="a" amount={h.amount} currency={h.currency} className="font-semibold" />],
              [t('billing.holdCol.purpose'), <Label key="p" group="holdPurpose" value={h.purpose} />],
              [t('hold.field.reserved'), h.domain_name ? <DomainLink key="d" keyOrName={h.domain_name} /> : <Label key="d" group="resourceType" value={h.resource_type} />],
              [t('common.customer'), item ? <CustomerRef key="c" id={item.customer_id} name={item.customer_name} /> : <AccountOwner key="c" account={h} />],
              [t('common.order'), <OrderRef key="o" id={h.order_id} domain={h.order_domain} count={h.order_item_count} at={h.order_placed_at} />],
              [t('hold.field.account'), account ? <AccountRef key="ac" id={account.id} code={account.code} ownerName={account.owner_name} currency={account.currency} /> : <span key="ac" className="text-muted-foreground">{t('hold.noAccount')}</span>],
              [t('hold.field.capture'), <JournalRef key="cap" id={h.capture_entry_id} description={capture?.description} entryType={capture?.entry_type} />],
              [t('hold.field.expires'), h.expires_at ? formatDateTime(h.expires_at) : null],
              [t('hold.field.closed'), h.closed_at ? formatDateTime(h.closed_at) : null],
              [t('common.created'), formatDateTime(h.created_at)],
              [t('hold.field.id'), <CopyText key="id" value={h.id} />],
            ]}
          />
        </Section>
        <Section title={t('hold.item')}>
          {item ? (
            <KeyValue
              columns={3}
              rows={[
                [t('common.domain'), <DomainLink key="d" keyOrName={item.domain_name} />],
                [t('common.operation'), <span key="op"><Label group="operation" value={item.operation} /> · {t('common.yearsLong', { count: item.years })}</span>],
                [t('hold.itemField.status'), <Status key="s" group="orderItemStatus" tones={ORDER_ITEM_STATUS} value={item.status} />],
                [t('hold.itemField.amount'), <Amount key="a" amount={item.amount} currency={item.currency} />],
                [t('common.order'), <OrderRef key="o" id={h.order_id} domain={h.order_domain} count={h.order_item_count} at={h.order_placed_at} />],
                [
                  t('hold.itemField.quote'),
                  <Link key="q" to={quotePath(item.quote_id)} className="font-medium text-primary hover:underline">
                    {t('order.viewQuote')}
                  </Link>,
                ],
              ]}
            />
          ) : (
            <p className="text-sm text-muted-foreground">{t('hold.noItem')}</p>
          )}
        </Section>
        <Section title={t('hold.entries')} flush>
          <JournalTable entries={journal_entries} empty={<EmptyState title={t('hold.noEntries')} />} />
        </Section>
      </div>
    </>
  );
}
